/**
 * Constants representing the different save states for the quiz.
 */

export const CONFLICT_ERROR_STATUS = 409;
export const SERVER_ERROR_STATUS = 500;

export type AuthoringState = {
  id: number;
  message: string | JSX.Element;
  continueEdit?: boolean | undefined;
  isTransient?: boolean;
};

export const Loading: AuthoringState = {
  id: 0,
  message: '',
};

export const Idle: AuthoringState = {
  id: 1,
  message: '',
};

export const InProgress: AuthoringState = {
  id: 2,
  message: 'Saving...',
};

export const Success: AuthoringState = {
  id: 3,
  message: '',
};

export const Error: AuthoringState = {
  id: 4,
  message: 'Error occurred while saving your changes.',
};

export const Conflict: AuthoringState = {
  id: 5,
  message: '',
};

export const PublishInProgress: AuthoringState = {
  id: 6,
  message: '',
};

export const Published: AuthoringState = {
  id: 7,
  message: 'Changes are published.',
};

export const NoChanges: AuthoringState = {
  id: 8,
  message: '',
};

export const RevertInProgress: AuthoringState = {
  id: 9,
  message: '',
};

export const Reverted: AuthoringState = {
  id: 10,
  message: '',
};

export const PublishError: AuthoringState = {
  id: 11,
  message: 'Error occurred.',
};

export const PublishSlugError: AuthoringState = {
  id: 12,
  message: 'Error occurred. The title must include at least one alphanumeric character',
};

export const RevertError: AuthoringState = {
  id: 13,
  message: 'Error occurred.',
};

export const LoadError: AuthoringState = {
  id: 14,
  message: 'Error occurred while loading.',
};

/**
 * this state is used for unversioned changes to assignments
 * unversioned changes are changes that get published right away when you save
 * for example, feedback type and default grade visibility
 */
export const SaveSuccess: AuthoringState = {
  id: 14,
  message: 'Changes are saved.',
};

export const PublishInvalid: AuthoringState = {
  id: 16,
  message: 'Please fix authoring errors.',
  isTransient: true,
};

/**
 * Enum-like object representing various states in the authoring process.
 * These states can be used to track and manage the lifecycle of an authoring operation.
 *
 * States:
 * - `Loading`: Indicates that the authoring process is in a loading state.
 * - `Idle`: Represents an idle state where no authoring activity is occurring.
 * - `InProgress`: Denotes that an authoring operation is currently in progress.
 * - `Success`: Indicates that the authoring operation completed successfully.
 * - `Error`: Represents a generic error state during the authoring process.
 * - `Conflict`: Denotes a conflict state, such as versioning conflict with course materials or items.
 * - `PublishInProgress`: Indicates that a publish operation is currently in progress.
 * - `Published`: Represents a state where the content has been successfully published.
 * - `NoChanges`: Indicates that there are no changes to save or publish.
 * - `RevertInProgress`: Denotes that a revert operation is currently in progress.
 * - `Reverted`: Indicates that the content has been successfully reverted.
 * - `PublishError`: Represents an error that occurred during the publish operation.
 * - `PublishSlugError`: Denotes an error related to the publish slug.
 * - `RevertError`: Indicates an error that occurred during the revert operation.
 * - `LoadError`: Represents an error that occurred while loading data.
 * - `SaveSuccess`: Indicates that the content was successfully saved.
 * - `PublishInvalid`: Denotes an invalid state for publishing.
 */
export const AuthoringStates = {
  Loading,
  Idle,
  InProgress,
  Success,
  Error,
  Conflict,
  PublishInProgress,
  Published,
  NoChanges,
  RevertInProgress,
  Reverted,
  PublishError,
  PublishSlugError,
  RevertError,
  LoadError,
  SaveSuccess,
  PublishInvalid,
};

export default AuthoringStates;
